import {useFirmwareDeleteMutation, useFirmwareListQuery, useFirmwareStateListQuery} from "../../app/api/api";
import {ColumnDef, FilterDef, FilterOperator, FilterType} from "../../components/ExTable";
import {useTranslation} from "react-i18next";
import {formatTimestamp} from "../../utils/datetime";
import {useModal} from "../../hooks/useModal";
import {useSnackbar} from "notistack";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";
import {AddCircleOutline as CreateIcon, Delete as DeleteIcon, UploadFile as UploadIcon} from "@mui/icons-material";
import {Firmware, FirmwareField} from "../../app/api/model/firmware";
import Page from "../../components/Layout/Page";
import {getTranslationKey, TranslationContext} from "../../utils/getTranslationKey";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../types";
import FirmwareUploadFileDialog from "./generalTab/FirmwareUploadFileDialog";
import {useNavigate} from "react-router-dom";
import {AddEditFirmwareDialog} from "./components/AddEditFirmwareDialog";
import {ListRequestIdModelTable} from "../../components/ExTable/variants";
import {usePermission} from "../../hooks/usePermission";
import {PermissionLevel} from "../../app/api/model/user";

export default function FirmwareList() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const deleteModal = useModal(false);
    const createModal = useModal(false);
    const uploadModal = useModal(false);
    const {enqueueSnackbar} = useSnackbar();
    const [deleteFirmware] = useFirmwareDeleteMutation();
    const {data: stateListResponse} = useFirmwareStateListQuery({});
    const {hasPermission} = usePermission();
    const columnDefs: ColumnDef<Firmware>[] = [
        {
            fieldName: FirmwareField.ID,
            align: "left",
            label: t("generic_id"),
            width: 80,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.FIRMWARES),
            sticky: true,
        },
        {
            fieldName: FirmwareField.HARDWARE_TYPE,
            align: "left",
            label: t("firmware_hardwareType"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: FirmwareField.TARGET,
            align: "left",
            label: t("firmware_target"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: FirmwareField.STATE,
            align: "left",
            label: t("firmware_state"),
            width: 100,
            sortable: true,
            formatCellText: ({cellValue}) =>
                t(getTranslationKey({context: TranslationContext.FIRMWARE_STATE, value: cellValue})),
        },
        {
            fieldName: FirmwareField.LEGACY,
            align: "left",
            label: t("firmware_legacy"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) => (row[FirmwareField.LEGACY] ? t("generic_yes") : t("generic_no")),
        },
        {
            fieldName: FirmwareField.CHECKSUM,
            align: "left",
            label: t("firmware_checksum"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: FirmwareField.S3_KEY,
            align: "left",
            label: t("firmware_s3Key"),
            width: 120,
            sortable: true,
        },
        {
            fieldName: FirmwareField.VERSION_NUMBER,
            align: "left",
            label: t("firmware_version"),
            width: 120,
            sortable: true,
        },
        {
            fieldName: FirmwareField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => formatTimestamp(cellValue),
        },
        {
            fieldName: FirmwareField.UPDATED_AT,
            align: "left",
            label: t("generic_updatedAt"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => formatTimestamp(cellValue),
        },
    ];

    const filterDefs: FilterDef<Firmware>[] = [
        {
            fieldName: FirmwareField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: FirmwareField.HARDWARE_TYPE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: FirmwareField.TARGET,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: FirmwareField.CHECKSUM,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: FirmwareField.S3_KEY,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: FirmwareField.VERSION_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: FirmwareField.STATE,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues:
                stateListResponse?.payload?.map((s) => {
                    return {
                        value: s,
                        labelKey: getTranslationKey({context: TranslationContext.FIRMWARE_STATE, value: s}),
                    };
                }) ?? [],
        },
        {
            fieldName: FirmwareField.LEGACY,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: [
                {value: "true", labelKey: "generic_yes"},
                {value: "false", labelKey: "generic_no"},
            ],
        },
        {
            fieldName: FirmwareField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: FirmwareField.UPDATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
    ];

    const handleDelete = (id: number) => {
        deleteModal.onOpen({id});
    };

    const commitDelete = async () => {
        await deleteFirmware(deleteModal.payload?.id as number).unwrap();
        enqueueSnackbar(t("generic_deleteSuccessfully"), {variant: "success"});
        deleteModal.toggleIsOpen();
    };

    const handleUpload = (id: number) => {
        uploadModal.onOpen({id});
    };

    const handleCreateSuccess = (id?: number) => {
        if (id) {
            navigate(`/firmwares/${id}?uploadFirmware=true`);
        }
    };

    return (
        <Page>
            <ListRequestIdModelTable<Firmware>
                dataTest="firmwaresList"
                queryHook={useFirmwareListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_firmwares")}
                renderActions={(keys) => (
                    <>
                        {keys.length === 1 && hasPermission(PermissionLevel.ADMIN) && (
                            <>
                                <Tooltip title={t("action_deleteFirmware")}>
                                    <IconButton onClick={() => handleDelete(keys[0].id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t("action_uploadFirmware")}>
                                    <IconButton onClick={() => handleUpload(keys[0].id)}>
                                        <UploadIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        {hasPermission(PermissionLevel.ADMIN) && (
                            <Tooltip title={t("action_createFirmware")}>
                                <IconButton onClick={createModal.toggleIsOpen}>
                                    <CreateIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}
            />
            <AddEditFirmwareDialog modal={createModal} onSuccess={handleCreateSuccess} />
            <FirmwareUploadFileDialog firmwareId={uploadModal.payload?.id} modal={uploadModal} />
            <Dialog
                open={deleteModal.isOpen}
                onClose={deleteModal.toggleIsOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t("generic_confirmDelete")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("confirm_deleteRecordWithId", {id: deleteModal.payload?.id})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteModal.toggleIsOpen}>{t("action_cancel")}</Button>
                    <Button onClick={commitDelete} autoFocus color="error">
                        {t("action_deleteFirmware")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Page>
    );
}
